import { useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useRef } from 'react'
import { FileRejection } from 'react-dropzone'

import { ReceiptAutoForwardInfoModal } from '@features/attachments/elements/ReceiptAutoForwardInfoModal'
import { useUserOrganizationSettings } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../enums/modalId'
import { useOrgUsesReceiptsOnly } from '../../../hooks/useOrgUsesReceiptsOnly'

interface ReceiptUploadGateContextValue {
  gateUpload: (onProceed: () => void) => void
}

const ReceiptUploadGateContext = createContext<ReceiptUploadGateContextValue | null>(null)

export const ReceiptUploadGateProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()
  const {
    isLoading: isOrganizationSettingsLoading,
    isPreviousData: isOrganizationSettingsPreviousData,
    organizationSettings,
  } = useUserOrganizationSettings()
  const { open: openReceiptAutoForwardInfoModal } = useModal(ModalId.ReceiptAutoForwardInfoModal)
  const onProceedRef = useRef<(() => void) | null>(null)

  const areOrganizationSettingsReady = !isOrganizationSettingsLoading && !isOrganizationSettingsPreviousData

  const shouldGateUpload =
    orgUsesReceiptsOnly &&
    (!areOrganizationSettingsReady || organizationSettings?.isAutoForwardInfoModalDismissed !== '1')

  const gateUpload = useCallback(
    (onProceed: () => void) => {
      if (!shouldGateUpload) {
        onProceed()
        return
      }

      onProceedRef.current = onProceed
      openReceiptAutoForwardInfoModal()
    },
    [openReceiptAutoForwardInfoModal, shouldGateUpload],
  )

  const handleModalAccept = useCallback(() => {
    onProceedRef.current?.()
    onProceedRef.current = null
  }, [])

  const handleModalClose = useCallback(() => {
    onProceedRef.current = null
  }, [])

  const value = useMemo(
    () => ({
      gateUpload,
    }),
    [gateUpload],
  )

  return (
    <ReceiptUploadGateContext.Provider value={value}>
      {children}
      {orgUsesReceiptsOnly ? (
        <ReceiptAutoForwardInfoModal
          id={ModalId.ReceiptAutoForwardInfoModal}
          onAccept={handleModalAccept}
          onDismiss={handleModalClose}
        />
      ) : null}
    </ReceiptUploadGateContext.Provider>
  )
}

export const useReceiptUploadGate = (): ReceiptUploadGateContextValue => {
  const context = useContext(ReceiptUploadGateContext)

  if (!context) {
    throw new Error('useReceiptUploadGate must be used within a ReceiptUploadGateProvider')
  }

  return context
}

export const useGatedUpload = (upload: (acceptedFiles: File[], rejectedFiles?: FileRejection[]) => void) => {
  const { gateUpload } = useReceiptUploadGate()

  return useCallback(
    (acceptedFiles: File[], rejectedFiles?: FileRejection[]) => {
      gateUpload(() => upload(acceptedFiles, rejectedFiles))
    },
    [gateUpload, upload],
  )
}
